import React, { useMemo } from "react";
import { daysArray } from "./helpers";
import { FaCrown } from "react-icons/fa";

/**
 * Tableau de présence pour le mois sélectionné.
 * - highlightIds: IDs des adhérents à mettre en avant (meilleure assiduité)
 * - onToggleCell(adherentId, dayDD, currentValue) : édition directe (optionnelle)
 */
const AttendanceHeatmap = ({
  month,
  rows = [],
  loading,
  highlightIds = [],
  onToggleCell, // optional
}) => {
  const days = useMemo(() => (month ? daysArray(month) : []), [month]);
  const highlightSet = useMemo(() => new Set(highlightIds || []), [highlightIds]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="animate-pulse h-12 rounded-xl bg-slate-200/70 dark:bg-slate-800/50"
          />
        ))}
      </div>
    );
  }

  if (!month || rows.length === 0) return null;

  return (
    <div className="overflow-x-auto w-full">
      <table className="min-w-full text-sm border-separate border-spacing-0">
        <thead className="sticky top-0 z-10">
          <tr>
            {/* Colonne Nom/Prénom */}
            <th className="sticky left-0 z-20 bg-white dark:bg-slate-900 px-2 py-2 text-left font-semibold ring-1 ring-slate-200 dark:ring-slate-800">
              Nom Prénom
            </th>
            {/* Jours */}
            {days.map((d) => (
              <th
                key={d}
                className="px-1 py-1 text-[11px] font-medium bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-800"
              >
                {d}
              </th>
            ))}
            <th className="px-2 py-2 font-semibold bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-800">
              Total
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r) => {
            const isTop = highlightSet.has(r.id);

            const nameCellClasses = [
              "sticky left-0 z-10 px-2 py-2 align-middle ring-1 ring-slate-200 dark:ring-slate-800",
              "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100",
              isTop ? "border-l-4 border-amber-400 bg-amber-50/60 dark:bg-amber-900/20" : ""
            ].join(" ");

            return (
              <tr
                key={r.id}
                className="hover:bg-slate-50/60 dark:hover:bg-slate-900/40"
              >
                {/* Nom / Prénom avec badge couronne si top */}
                <td className={nameCellClasses}>
                  <div className="flex items-center gap-2">
                    {isTop && (
                      <span
                        className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-400 text-white ring-1 ring-amber-500/50"
                        title="Meilleure assiduité du mois"
                        aria-label="Meilleure assiduité du mois"
                      >
                        <FaCrown className="text-[11px]" />
                      </span>
                    )}
                    <span className="font-medium">
                      {(r.nom || "").toUpperCase()} {r.prenom || ""}
                    </span>
                  </div>
                </td>

                {/* Jours */}
                {days.map((d) => {
                  const v = r.dates?.[d]; // true / false / undefined
                  const base = "w-6 h-6 md:w-7 md:h-7";
                  const cellClass =
                    v === true
                      ? "bg-emerald-500/85"
                      : v === false
                      ? "bg-rose-500/85"
                      : "bg-transparent";

                  const label =
                    v === true ? "Présent" : v === false ? "Absent" : "Non saisi";

                  return (
                    <td
                      key={d}
                      className={`text-center align-middle ring-1 ring-slate-200 dark:ring-slate-800`}
                      title={label}
                    >
                      {onToggleCell ? (
                        <button
                          type="button"
                          onClick={() => onToggleCell(r.id, d, v)}
                          aria-label={`${label} — cliquer pour changer`}
                          className={`block ${base} mx-auto rounded-sm ${cellClass} hover:outline hover:outline-2 hover:outline-slate-400/50 focus:outline focus:outline-2 focus:outline-violet-500`}
                        />
                      ) : (
                        <div className={`${base} mx-auto rounded-sm ${cellClass}`} />
                      )}
                    </td>
                  );
                })}

                {/* Total présents du mois */}
                <td className="text-center align-middle ring-1 ring-slate-200 dark:ring-slate-800 font-semibold pl-4">
                  {r.totalPresent}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Note en cas d'ex æquo */}
      {highlightIds.length > 1 && (
        <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          Plusieurs adhérents ex æquo à la meilleure assiduité ce mois-ci.
        </div>
      )}
    </div>
  );
};

export default AttendanceHeatmap;
